import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  createChatGptResult,
  deleteChatGptResult,
  fetchDiarizationMeta,
  fetchDiarizedUtterances,
  fetchPlayerUrl,
  fetchTranscription,
  fetchTranscriptions,
  fetchTranscriptMeta,
  fetchTranscriptSegments,
  listChatGptResults,
  retryDiarization,
  retryMediaProcessing,
  retryTranscription,
  purgeTranscription,
  assignSpeaker,
  updateChatGptResult,
  updateMeeting,
  type AssignSpeakerInput,
  type ChatGptResult,
  type ChatGptResultSummary,
  type CreateChatGptResultInput,
  type DiarizationMeta,
  type PlayerUrl,
  type TranscriptMeta,
  type TranscriptionDetail,
  type TranscriptionListFilters,
  type TranscriptionsPage,
  type UpdateChatGptResultInput,
  type UpdateMeetingInput,
} from './api';
import {
  describeStatus,
  isDiarizationAvailableStatus,
  isPlayerReadyStatus,
  isTranscriptAvailableStatus,
  shouldPollDetail,
} from './status';

function toChatGptSummary(result: ChatGptResult): ChatGptResultSummary {
  return {
    id: result.id,
    transcriptionId: result.transcriptionId,
    promptId: result.promptId,
    promptLabel: result.promptLabel,
    title: result.title,
    createdById: result.createdById,
    createdBy: result.createdBy,
    createdAt: result.createdAt,
    updatedAt: result.updatedAt,
  };
}

export const transcriptionKeys = {
  all: ['transcriptions'] as const,
  list: (filters: TranscriptionListFilters = {}) =>
    [...transcriptionKeys.all, 'list', filters] as const,
  detail: (id: string) => [...transcriptionKeys.all, 'detail', id] as const,
  playerUrl: (id: string) => [...transcriptionKeys.all, 'player-url', id] as const,
  transcriptMeta: (id: string) => [...transcriptionKeys.all, 'transcript-meta', id] as const,
  transcriptSegments: (id: string) => [...transcriptionKeys.all, 'transcript-segments', id] as const,
  diarizationMeta: (id: string) => [...transcriptionKeys.all, 'diarization-meta', id] as const,
  diarizationSegments: (id: string) =>
    [...transcriptionKeys.all, 'diarization-segments', id] as const,
  chatGptResults: (id: string) => [...transcriptionKeys.all, 'chatgpt-results', id] as const,
};

export function useTranscriptionsQuery(
  filters: TranscriptionListFilters = {},
): UseQueryResult<TranscriptionsPage> {
  return useQuery({
    queryKey: transcriptionKeys.list(filters),
    queryFn: ({ signal }) => fetchTranscriptions(filters, signal),
    refetchInterval: (query) => {
      const page = query.state.data;
      if (page === undefined) {
        return false;
      }
      return page.items.some((item) => describeStatus(item.status).inProgress) ? 10_000 : false;
    },
  });
}

export function useTranscriptionQuery(id: string | undefined): UseQueryResult<TranscriptionDetail> {
  return useQuery({
    queryKey: transcriptionKeys.detail(id ?? ''),
    queryFn: ({ signal }) => fetchTranscription(id ?? '', signal),
    enabled: id !== undefined && id !== '',
    refetchInterval: (query) => {
      const status = query.state.data?.status ?? null;
      return shouldPollDetail(status) ? 3_000 : false;
    },
  });
}

export function usePlayerUrlQuery(
  id: string | undefined,
  status: string | null | undefined,
): UseQueryResult<PlayerUrl> {
  return useQuery({
    queryKey: transcriptionKeys.playerUrl(id ?? ''),
    queryFn: ({ signal }) => fetchPlayerUrl(id ?? '', signal),
    enabled: id !== undefined && id !== '' && isPlayerReadyStatus(status ?? null),
    staleTime: 60_000,
    retry: (failureCount) => failureCount < 2,
  });
}

export function useRetryMediaProcessing(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => retryMediaProcessing(id ?? ''),
    onSuccess: async () => {
      if (id === undefined || id === '') {
        return;
      }
      await queryClient.invalidateQueries({ queryKey: transcriptionKeys.detail(id) });
    },
  });
}

export function useRetryTranscription(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => retryTranscription(id ?? ''),
    onSuccess: async () => {
      if (id === undefined || id === '') {
        return;
      }
      await queryClient.invalidateQueries({ queryKey: transcriptionKeys.detail(id) });
    },
  });
}

export function useRetryDiarization(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ force = false }: { force?: boolean } = {}) => retryDiarization(id ?? '', force),
    onSuccess: async () => {
      if (id === undefined || id === '') {
        return;
      }
      await queryClient.invalidateQueries({ queryKey: transcriptionKeys.detail(id) });
    },
  });
}

export function useTranscriptMetaQuery(
  id: string | undefined,
  status: string | null | undefined,
): UseQueryResult<TranscriptMeta> {
  return useQuery({
    queryKey: transcriptionKeys.transcriptMeta(id ?? ''),
    queryFn: ({ signal }) => fetchTranscriptMeta(id ?? '', signal),
    enabled: id !== undefined && id !== '' && isTranscriptAvailableStatus(status ?? null),
    staleTime: 60_000,
  });
}

export function useDiarizationMetaQuery(
  id: string | undefined,
  status: string | null | undefined,
): UseQueryResult<DiarizationMeta> {
  return useQuery({
    queryKey: transcriptionKeys.diarizationMeta(id ?? ''),
    queryFn: ({ signal }) => fetchDiarizationMeta(id ?? '', signal),
    enabled: id !== undefined && id !== '' && isDiarizationAvailableStatus(status ?? null),
    staleTime: 60_000,
  });
}

const SEGMENTS_PAGE_LIMIT = 100;

export function useTranscriptSegmentsInfiniteQuery(
  id: string | undefined,
  status: string | null | undefined,
) {
  return useInfiniteQuery({
    queryKey: transcriptionKeys.transcriptSegments(id ?? ''),
    queryFn: ({ pageParam, signal }) =>
      fetchTranscriptSegments(
        id ?? '',
        { limit: SEGMENTS_PAGE_LIMIT, ...(pageParam !== null ? { cursor: pageParam } : {}) },
        signal,
      ),
    enabled: id !== undefined && id !== '' && isTranscriptAvailableStatus(status ?? null),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 60_000,
  });
}

export function useDiarizedUtterancesInfiniteQuery(
  id: string | undefined,
  status: string | null | undefined,
) {
  return useInfiniteQuery({
    queryKey: transcriptionKeys.diarizationSegments(id ?? ''),
    queryFn: ({ pageParam, signal }) =>
      fetchDiarizedUtterances(
        id ?? '',
        { limit: SEGMENTS_PAGE_LIMIT, ...(pageParam !== null ? { cursor: pageParam } : {}) },
        signal,
      ),
    enabled: id !== undefined && id !== '' && isDiarizationAvailableStatus(status ?? null),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 60_000,
  });
}

export function useAssignSpeaker(transcriptionId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ speakerId, input }: { speakerId: string; input: AssignSpeakerInput }) =>
      assignSpeaker(transcriptionId ?? '', speakerId, input),
    onSuccess: async () => {
      if (transcriptionId === undefined || transcriptionId === '') {
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: transcriptionKeys.diarizationMeta(transcriptionId) }),
        queryClient.invalidateQueries({ queryKey: transcriptionKeys.diarizationSegments(transcriptionId) }),
      ]);
    },
  });
}

export function usePurgeTranscriptionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => purgeTranscription(id),
    onSuccess: async (_data, id) => {
      await queryClient.invalidateQueries({ queryKey: transcriptionKeys.all });
      await queryClient.removeQueries({ queryKey: transcriptionKeys.detail(id) });
    },
  });
}

export function useUpdateMeetingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateMeetingInput }) => updateMeeting(id, body),
    onSuccess: async (_updated, { id }) => {
      await queryClient.invalidateQueries({ queryKey: transcriptionKeys.all });
      await queryClient.invalidateQueries({ queryKey: transcriptionKeys.detail(id) });
    },
  });
}

export function useChatGptResultsQuery(transcriptionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: transcriptionKeys.chatGptResults(transcriptionId ?? ''),
    queryFn: ({ signal }) => listChatGptResults(transcriptionId ?? '', signal),
    enabled: enabled && transcriptionId !== undefined && transcriptionId !== '',
  });
}

export function useCreateChatGptResultMutation(transcriptionId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateChatGptResultInput) => createChatGptResult(transcriptionId ?? '', body),
    onSuccess: (created) => {
      if (transcriptionId === undefined || transcriptionId === '') {
        return;
      }
      const key = transcriptionKeys.chatGptResults(transcriptionId);
      queryClient.setQueryData<{ items: ChatGptResultSummary[] }>(key, (previous) => {
        const summary = toChatGptSummary(created);
        if (previous === undefined) {
          return { items: [summary] };
        }
        return { items: [summary, ...previous.items.filter((item) => item.id !== summary.id)] };
      });
    },
  });
}

export function useUpdateChatGptResultMutation(transcriptionId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ resultId, body }: { resultId: string; body: UpdateChatGptResultInput }) =>
      updateChatGptResult(transcriptionId ?? '', resultId, body),
    onSuccess: (updated) => {
      if (transcriptionId === undefined || transcriptionId === '') {
        return;
      }
      const key = transcriptionKeys.chatGptResults(transcriptionId);
      queryClient.setQueryData<{ items: ChatGptResultSummary[] }>(key, (previous) => {
        const summary = toChatGptSummary(updated);
        if (previous === undefined) {
          return { items: [summary] };
        }
        return {
          items: previous.items.map((item) => (item.id === summary.id ? summary : item)),
        };
      });
    },
  });
}

export function useDeleteChatGptResultMutation(transcriptionId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resultId: string) => deleteChatGptResult(transcriptionId ?? '', resultId),
    onSuccess: (_data, resultId) => {
      if (transcriptionId === undefined || transcriptionId === '') {
        return;
      }
      const key = transcriptionKeys.chatGptResults(transcriptionId);
      queryClient.setQueryData<{ items: ChatGptResultSummary[] }>(key, (previous) => {
        if (previous === undefined) {
          return previous;
        }
        return { items: previous.items.filter((item) => item.id !== resultId) };
      });
    },
  });
}
